import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "./api";
import { logDev } from "./log";

const BrandingContext = createContext(null);

const DEFAULT = {
  company_name: "liveprocure.com",
  tagline: "Construction Ops",
  logo_url: null,
};

function absolutize(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/branding");
      setBranding({
        company_name: data.company_name || DEFAULT.company_name,
        tagline: data.tagline || DEFAULT.tagline,
        logo_url: absolutize(data.logo_url),
      });
    } catch (e) {
      logDev("branding.load", e);
      // non-fatal — falls back to default branding
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (patch) => {
    try {
      const { data } = await api.patch("/settings/branding", patch);
      setBranding({
        company_name: data.company_name || DEFAULT.company_name,
        tagline: data.tagline || DEFAULT.tagline,
        logo_url: absolutize(data.logo_url),
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || "Update failed" };
    }
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/settings/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBranding({
        company_name: data.company_name || DEFAULT.company_name,
        tagline: data.tagline || DEFAULT.tagline,
        logo_url: absolutize(data.logo_url),
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || "Upload failed" };
    }
  };

  const value = useMemo(
    () => ({ branding, update, uploadLogo, refresh: load }),
    [branding, load],
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error("useBranding must be used within BrandingProvider");
  return ctx;
}
